import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Card } from '@reflex/ui/components/ui/card';
import { SettingsRow } from '@reflex/ui/components/shared/SettingsRow';
import { Switch } from '@reflex/ui/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useFeatureFlags } from '@reflex/ui/client/api/feature-flags';
import { useActiveOrgId } from '@/hooks/auth/useActiveOrgId';
import type { OrgId } from '@reflex/ui/client/api/org-id';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { SectionLabel, PageDescription } from '@reflex/ui/components/layout/Typography';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { cn } from '@reflex/ui/lib/utils';
import { useSetFlagMutation } from './admin-flags-shared';
import type { FeatureFlag, FlagUserOverride, User } from '@/lib/types';
import { getUserDisplayName } from '@/lib/agent-utils';

export function FeatureFlagsPanel() {
  const org = useActiveOrgId();
  if (!org) {
    return (
      <div>
        <SectionLabel className="mb-3">Feature Flags</SectionLabel>
        <PageDescription className="text-xs">No active organization.</PageDescription>
      </div>
    );
  }
  return <FeatureFlagsPanelBody org={org} />;
}

function FeatureFlagsPanelBody({ org }: { org: OrgId }) {
  const flagsQuery = useFeatureFlags(org);
  const setFlagMutation = useSetFlagMutation(org);
  const flags = flagsQuery.data ?? [];

  return (
    <div>
      <SectionLabel className="mb-3">Feature Flags</SectionLabel>
      <PageDescription className="mb-4 text-xs">
        Toggle experimental and rollout features. Changes take effect immediately for new
        operations.
      </PageDescription>
      <AsyncContent
        isLoading={flagsQuery.isLoading}
        isError={flagsQuery.isError}
        loadingMessage="Loading flags…"
        errorMessage="Failed to load feature flags."
      >
        <div className="space-y-4">
          {flags.map((flag: FeatureFlag) => (
            <FlagCard
              key={flag.key}
              flag={flag}
              onToggle={(enabled) => setFlagMutation.mutate({ key: flag.key, enabled })}
              disabled={setFlagMutation.isPending}
            />
          ))}
        </div>
      </AsyncContent>
    </div>
  );
}

export function AdminFlagsPage() {
  const org = useActiveOrgId();
  if (!org) {
    return (
      <AdminSubPageLayout
        title="Feature Flags"
        description="Toggle experimental and rollout features. Changes take effect immediately for new operations."
      >
        <PageDescription className="text-xs">No active organization.</PageDescription>
      </AdminSubPageLayout>
    );
  }
  return <AdminFlagsPageBody org={org} />;
}

function AdminFlagsPageBody({ org }: { org: OrgId }) {
  const flagsQuery = useFeatureFlags(org);
  const setFlagMutation = useSetFlagMutation(org);

  const flags = flagsQuery.data ?? [];
  const globalFlags = flags.filter((f: FeatureFlag) => f.scope === 'global');
  const accountFlags = flags.filter((f: FeatureFlag) => f.scope === 'account');

  return (
    <AdminSubPageLayout
      title="Feature Flags"
      description="Toggle experimental and rollout features. Changes take effect immediately for new operations."
    >
      <AsyncContent
        isLoading={flagsQuery.isLoading}
        isError={flagsQuery.isError}
        loadingMessage="Loading flags…"
        errorMessage="Failed to load feature flags."
      >
        <div className="space-y-6">
          {globalFlags.length > 0 && (
            <div className="space-y-3">
              <SectionLabel>Global</SectionLabel>
              {globalFlags.map((flag: FeatureFlag) => (
                <FlagCard
                  key={flag.key}
                  flag={flag}
                  onToggle={(enabled) => setFlagMutation.mutate({ key: flag.key, enabled })}
                  disabled={setFlagMutation.isPending}
                />
              ))}
            </div>
          )}
          {accountFlags.length > 0 && (
            <div className="space-y-3">
              <SectionLabel>Account-scoped</SectionLabel>
              <PageDescription className="text-xs -mt-1">
                The toggle sets the global default. Use the user picker to enable the flag for
                specific accounts.
              </PageDescription>
              {accountFlags.map((flag: FeatureFlag) => (
                <AccountFlagCard
                  key={flag.key}
                  flag={flag}
                  onToggle={(enabled) => setFlagMutation.mutate({ key: flag.key, enabled })}
                  disabled={setFlagMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </AsyncContent>
    </AdminSubPageLayout>
  );
}

export function FlagCard({
  flag,
  onToggle,
  disabled,
}: {
  flag: FeatureFlag;
  onToggle: (enabled: boolean) => void;
  disabled: boolean;
}) {
  return (
    <SettingsRow
      title={flag.label}
      description={flag.description}
      badges={
        <>
          <Badge variant="secondary" className="text-xs font-normal">
            default: {flag.defaultValue ? 'on' : 'off'}
          </Badge>
          <Badge variant="outline" className="text-xs font-normal">
            {flag.scope}
          </Badge>
        </>
      }
      control={
        <Switch
          checked={flag.enabled}
          onCheckedChange={onToggle}
          disabled={disabled}
          aria-label={`Toggle ${flag.label}`}
        />
      }
    />
  );
}

function AccountFlagCard({
  flag,
  onToggle,
  disabled,
}: {
  flag: FeatureFlag;
  onToggle: (enabled: boolean) => void;
  disabled: boolean;
}) {
  const queryClient = useQueryClient();

  const overridesQuery = useQuery({
    queryKey: [...queryKeys.flagOverrides(flag.key)],
    queryFn: () => api.getFlagOverrides(flag.key),
  });

  // Per-user flag overrides can target any user across the platform, so this
  // panel shares the admin-global users slot rather than the org-scoped
  // ['users', orgKey] slot owned by useUsers().
  const usersQuery = useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: () => api.listUsers(),
  });

  const setOverrideMutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      api.setFlagOverride(flag.key, userId, enabled),
    onSuccess: (data) => {
      queryClient.setQueryData([...queryKeys.flagOverrides(flag.key)], data);
    },
    onError: (err: Error) =>
      toast.error('Failed to update user override', { description: err.message }),
  });

  const clearOverrideMutation = useMutation({
    mutationFn: (userId: string) => api.clearFlagOverride(flag.key, userId),
    onSuccess: (data) => {
      queryClient.setQueryData([...queryKeys.flagOverrides(flag.key)], data);
    },
    onError: (err: Error) =>
      toast.error('Failed to remove user override', { description: err.message }),
  });

  const overrides = overridesQuery.data ?? [];
  const enabledUserIds = new Set(
    overrides.filter((o: FlagUserOverride) => o.enabled).map((o: FlagUserOverride) => o.userId),
  );

  return (
    <Card className="p-4 space-y-3">
      <SettingsRow
        variant="plain"
        title={flag.label}
        description={flag.description}
        badges={
          <>
            <Badge variant="secondary" className="text-xs font-normal">
              default: {flag.defaultValue ? 'on' : 'off'}
            </Badge>
            <Badge variant="outline" className="text-xs font-normal">
              {flag.scope}
            </Badge>
          </>
        }
        control={
          <Switch
            checked={flag.enabled}
            onCheckedChange={onToggle}
            disabled={disabled}
            aria-label={`Toggle ${flag.label}`}
          />
        }
      />

      <div className="border-t pt-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <SectionLabel as="span">Enabled for users</SectionLabel>
          <UserMultiSelect
            users={usersQuery.data ?? []}
            enabledUserIds={enabledUserIds}
            onAdd={(userId) => setOverrideMutation.mutate({ userId, enabled: true })}
            onRemove={(userId) => clearOverrideMutation.mutate(userId)}
            isPending={setOverrideMutation.isPending || clearOverrideMutation.isPending}
          />
        </div>

        {enabledUserIds.size > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {overrides
              .filter((o: FlagUserOverride) => o.enabled)
              .map((o: FlagUserOverride) => (
                <UserChip
                  key={o.userId}
                  override={o}
                  onRemove={() => clearOverrideMutation.mutate(o.userId)}
                  disabled={clearOverrideMutation.isPending}
                />
              ))}
          </div>
        ) : (
          <PageDescription className="mt-0 text-xs italic">
            No per-user overrides: the global default applies to all users.
          </PageDescription>
        )}
      </div>
    </Card>
  );
}

function UserChip({
  override,
  onRemove,
  disabled,
}: {
  override: FlagUserOverride;
  onRemove: () => void;
  disabled: boolean;
}) {
  const displayName = getUserDisplayName(override.user);
  return (
    <Badge variant="outline" className="gap-1.5 bg-muted/40 py-1 pl-1.5 pr-2 leading-none">
      <UserAvatar
        avatarUrl={override.user.avatarUrl ?? undefined}
        userName={displayName}
        size="xs"
      />
      <span className="max-w-[140px] truncate">{displayName}</span>
      <Button
        variant="ghost"
        size="icon-xs"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${displayName}`}
        className="rounded-full -mr-0.5"
      >
        <X className="h-3 w-3" />
      </Button>
    </Badge>
  );
}

function UserMultiSelect({
  users,
  enabledUserIds,
  onAdd,
  onRemove,
  isPending,
}: {
  users: User[];
  enabledUserIds: Set<string>;
  onAdd: (userId: string) => void;
  onRemove: (userId: string) => void;
  isPending: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label="Add users"
          disabled={isPending}
          className={cn(
            'flex h-7 items-center gap-1.5 rounded-md border border-input bg-transparent px-2.5 text-xs shadow-xs transition-[color,box-shadow] outline-none',
            'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
            'dark:bg-input/30 dark:hover:bg-input/50',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          <span>Add users</span>
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <Command>
          <CommandInput placeholder="Search users…" className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty className="py-3 text-xs">No users found</CommandEmpty>
            <CommandGroup>
              {users.map((user) => {
                const isEnabled = enabledUserIds.has(user.id);
                const displayName = getUserDisplayName(user);
                return (
                  <CommandItem
                    key={user.id}
                    value={`${user.login} ${user.name ?? ''} ${user.email}`}
                    onSelect={() => {
                      if (isEnabled) {
                        onRemove(user.id);
                      } else {
                        onAdd(user.id);
                      }
                    }}
                    className="text-xs gap-2"
                  >
                    <Check
                      className={cn('h-3 w-3 shrink-0', isEnabled ? 'opacity-100' : 'opacity-0')}
                    />
                    <UserAvatar
                      avatarUrl={user.avatarUrl ?? undefined}
                      userName={displayName}
                      size="sm"
                      className="h-5 w-5 text-3xs"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{displayName}</div>
                      <div className="truncate text-muted-foreground">{user.login}</div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
